
import { useState } from 'react';

interface RewardTier {
  id: string;
  title: string;
  price: number;
  originalPrice?: number;
  description: string;
  includes: string[];
  delivery: string;
  backers: number;
  limited?: number;
  popular?: boolean;
  earlyBird?: boolean;
}

export default function RewardTiers() {
  const [selectedTier, setSelectedTier] = useState<string>('');

  const rewardTiers: RewardTier[] = [
    {
      id: 'early-bird-single',
      title: 'Early Bird - Single Pack',
      price: 15,
      originalPrice: 25,
      description: 'Perfect for trying Marine Pad for the first time',
      includes: [
        '1 pack of Marine Pad (8 pads)',
        'Digital thank you card',
        'Exclusive updates on our journey'
      ],
      delivery: 'March 2025',
      backers: 127,
      limited: 500,
      earlyBird: true
    },
    {
      id: 'starter-pack',
      title: 'Starter Pack',
      price: 35,
      originalPrice: 50,
      description: 'Great value for new Marine Pad users',
      includes: [
        '3 packs of Marine Pad (24 pads total)',
        'Organic cotton storage pouch',
        'Digital sustainability guide',
        'Exclusive backer updates'
      ],
      delivery: 'March 2025',
      backers: 89,
      popular: true
    },
    {
      id: 'complete-care',
      title: 'Complete Care Bundle',
      price: 65,
      originalPrice: 95,
      description: 'Everything you need for sustainable period care',
      includes: [
        '6 packs of Marine Pad (48 pads total)',
        'Premium organic cotton storage pouch',
        'Marine Pad travel case',
        'Printed sustainability guide',
        'Exclusive Marine Pad stickers',
        'Priority customer support'
      ],
      delivery: 'March 2025',
      backers: 156,
      popular: true
    },
    {
      id: 'champion-supporter',
      title: 'Champion Supporter',
      price: 120,
      originalPrice: 180,
      description: 'For those who want to make a real impact',
      includes: [
        '12 packs of Marine Pad (96 pads total)',
        'Premium gift box packaging',
        'Signed thank you letter from founder',
        'Marine Pad branded reusable water bottle',
        'Exclusive webinar access with our science team',
        'Your name on our supporter wall'
      ],
      delivery: 'March 2025',
      backers: 43
    },
    {
      id: 'ocean-guardian',
      title: 'Ocean Guardian',
      price: 250,
      originalPrice: 350,
      description: 'Ultimate supporter package with exclusive perks',
      includes: [
        '24 packs of Marine Pad (192 pads total)',
        'Limited edition Marine Pad tote bag',
        'Personal video thank you from founder',
        'Exclusive Marine Pad merchandise bundle',
        'Early access to future products',
        'Invitation to virtual launch event',
        'Certificate of environmental impact'
      ],
      delivery: 'March 2025',
      backers: 12,
      limited: 100
    }
  ];

  const handleSelectTier = (tierId: string) => {
    setSelectedTier(tierId);
    // Scroll to newsletter signup for funding option selection
    document.getElementById('newsletter-signup')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="rewards" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-6">
            Support Marine Pad on Kickstarter
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Choose your reward tier and be among the first to experience the future of sustainable period care. 
            Every pledge helps us bring Marine Pad to market and create a better world.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {rewardTiers.map((tier) => (
            <div
              key={tier.id}
              className={`relative bg-white border-2 rounded-lg p-6 transition-all duration-300 hover:shadow-lg cursor-pointer ${
                tier.popular 
                  ? 'border-teal-500 shadow-lg' 
                  : selectedTier === tier.id 
                    ? 'border-teal-400' 
                    : 'border-gray-200 hover:border-gray-300'
              }`}
              onClick={() => handleSelectTier(tier.id)}
            >
              {/* Popular Badge */}
              {tier.popular && (
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  <span className="bg-teal-500 text-white px-4 py-1 rounded-full text-sm font-semibold">
                    Most Popular
                  </span>
                </div>
              )}

              {/* Early Bird Badge */}
              {tier.earlyBird && (
                <div className="absolute -top-3 right-4">
                  <span className="bg-orange-500 text-white px-3 py-1 rounded-full text-xs font-semibold">
                    Early Bird
                  </span>
                </div>
              )}

              {/* Limited Badge */}
              {tier.limited && (
                <div className="absolute top-4 right-4">
                  <span className="bg-red-100 text-red-600 px-2 py-1 rounded text-xs font-semibold">
                    Limited: {tier.limited - tier.backers} left
                  </span>
                </div>
              )}

              <div className="mb-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2">{tier.title}</h3>
                <div className="flex items-baseline mb-2">
                  <span className="text-3xl font-bold text-teal-600">${tier.price}</span>
                  {tier.originalPrice && (
                    <span className="ml-2 text-lg text-gray-500 line-through">
                      ${tier.originalPrice}
                    </span>
                  )}
                </div>
                <p className="text-gray-600 text-sm">{tier.description}</p>
              </div>

              <div className="mb-6">
                <h4 className="font-semibold text-gray-900 mb-3">What's Included:</h4>
                <ul className="space-y-2">
                  {tier.includes.map((item, index) => (
                    <li key={index} className="flex items-start text-sm text-gray-700">
                      <i className="ri-check-line text-teal-500 mr-2 mt-0.5 flex-shrink-0"></i>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="border-t pt-4">
                <div className="flex justify-between items-center text-sm text-gray-600 mb-3">
                  <span>Estimated Delivery:</span>
                  <span className="font-semibold">{tier.delivery}</span>
                </div>
                <div className="flex justify-between items-center text-sm text-gray-600 mb-4">
                  <span>Backers:</span>
                  <span className="font-semibold">{tier.backers}</span>
                </div>
                
                <button
                  className={`w-full py-3 px-4 rounded-lg font-semibold transition-colors whitespace-nowrap ${
                    selectedTier === tier.id
                      ? 'bg-teal-600 text-white'
                      : tier.popular
                        ? 'bg-teal-600 text-white hover:bg-teal-700'
                        : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                  }`}
                >
                  {selectedTier === tier.id ? 'Selected' : 'Select This Reward'}
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Campaign Stats */}
        <div className="mt-16 bg-gradient-to-r from-teal-50 to-blue-50 rounded-lg p-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 text-center">
            <div>
              <div className="text-3xl font-bold text-teal-600 mb-2">$47,892</div>
              <div className="text-gray-600">Raised so far</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-teal-600 mb-2">427</div>
              <div className="text-gray-600">Backers</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-teal-600 mb-2">23</div>
              <div className="text-gray-600">Days to go</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-teal-600 mb-2">48%</div>
              <div className="text-gray-600">Funded</div>
            </div>
          </div>
          
          {/* Progress Bar */}
          <div className="mt-6">
            <div className="bg-gray-200 rounded-full h-3">
              <div className="bg-gradient-to-r from-teal-500 to-blue-500 h-3 rounded-full" style={{ width: '48%' }}></div>
            </div>
            <div className="flex justify-between mt-2 text-sm text-gray-600">
              <span>Goal: $100,000</span>
              <span>48% Complete</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
